import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../helpers/redux';
import { RootState } from '../../../../app/store';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';
import AgDataGridServerPaging from '../../../../components/DataGrid/AgDataGridServerPaging';
import DefaultModal from '../../../../components/Modal/DefaultModal';
import { getHistory } from '../../history/history.reducer';
import { JobHistoryColumnSort, SortingEnum } from '../../history/models';
import {
    getPoleByJob,
    getPoleKatapultByJob,
    getPoleMREByJob,
} from '../../../basic/pole/pole.reducer';
import {
    JOB_STATUS_COMPLETE,
    JOB_STATUS_COMPLETE_KATAPULT,
    JOB_STATUS_COMPLETE_MRE,
} from '../../../../constants';

const PoleDetailRenderer = (props: any) => {
    const dispatch = useAppDispatch();
    const data = props.data;
    const [poles, setPoles] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const libraries: Libraries = useMemo(() => ['places'], []);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: 'AIzaSyDQ-aesOz_ut1yBv_d6FG-0N60uqDwgLZs',
        libraries: libraries,
    });

    const mapCenter = useMemo(() => {
        if (poles.length > 0 && poles[0].latitude && poles[0].longitude) {
            return {
                lat: Number(poles[0].latitude),
                lng: Number(poles[0].longitude),
            };
        }
        return { lat: 43.45, lng: -80.49 };
    }, [poles]);

    useEffect(() => {
        if (!data || !data.id) return;

        const fetchPoles = async () => {
            setLoading(true);
            try {
                // Determine which API to call based on Job Kind/Status/Koj
                let res;
                // Based on standard logic, MRE and Katapult have specific APIs, others use the general one.
                const koj = data.koj?.toUpperCase() || '';

                if (
                    koj === 'MRE' ||
                    koj === 'MRE REVISION' ||
                    data.status === JOB_STATUS_COMPLETE_MRE
                ) {
                    res = (await dispatch(
                        getPoleMREByJob({
                            job_id: data.id,
                            skip: 0,
                            take: 500, // limit for detail view preview
                        }),
                    )) as any;
                } else if (
                    koj === 'KATAPULT' ||
                    data.status === JOB_STATUS_COMPLETE_KATAPULT
                ) {
                    res = (await dispatch(
                        getPoleKatapultByJob({
                            job_id: data.id,
                            skip: 0,
                            take: 500,
                        }),
                    )) as any;
                } else {
                    res = (await dispatch(
                        getPoleByJob({
                            job_id: data.id,
                            skip: 0,
                            take: 500,
                            column: 5,
                            sort: SortingEnum.Ascending,
                        }),
                    )) as any;
                }

                let fetchedPoles = [];
                if (res?.payload?.data) {
                    fetchedPoles = res.payload.data;
                } else if (res?.payload) {
                    fetchedPoles = res.payload; // handle array payload directly
                }

                const currentFilters = props.getFilters
                    ? props.getFilters()
                    : {};

                console.log('currentFilters', currentFilters);

                if (currentFilters?.pole_types) {
                    const types = currentFilters.pole_types
                        .split(',')
                        .map((t: string) => t.toLowerCase());
                    fetchedPoles = fetchedPoles.filter(
                        (p: any) =>
                            types.includes(String(p.type_id).toLowerCase()) ||
                            types.includes(
                                String(p.pole_type_name).toLowerCase(),
                            ) ||
                            types.includes(String(p.type_name).toLowerCase()),
                    );
                }
                if (currentFilters?.pole_statuses) {
                    const statuses = currentFilters.pole_statuses
                        .split(',')
                        .map((s: string) => s.toLowerCase());
                    fetchedPoles = fetchedPoles.filter(
                        (p: any) =>
                            statuses.includes(
                                String(p.status_id).toLowerCase(),
                            ) ||
                            statuses.includes(
                                String(p.status_name).toLowerCase(),
                            ) ||
                            statuses.includes(
                                String(p.pole_status_name).toLowerCase(),
                            ),
                    );
                }

                setPoles(fetchedPoles);
            } catch (error) {
                console.error('Failed to fetch poles', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPoles();
    }, [data, dispatch]);

    if (loading) {
        return (
            <div className="p-4 text-center text-sm text-gray-500">
                Loading poles data...
            </div>
        );
    }

    if (poles.length === 0) {
        return (
            <div className="p-4 text-center text-sm text-gray-500">
                No poles data found for this job.
            </div>
        );
    }

    return (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg m-2 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                Pole Summary ({poles.length} poles)
            </h3>

            <div className="flex gap-4">
                <div className="flex-1 overflow-y-auto max-h-[160px] border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-left text-xs relative">
                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 font-medium">
                                    Pole Seq
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Pole No
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Is PIP
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Latitude
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Longitude
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Pole Type
                                </th>
                                <th className="px-3 py-2 font-medium">
                                    Pole Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                            {poles.map((pole, index) => (
                                <tr
                                    key={index}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                    <td className="px-3 py-2 font-medium text-indigo-600 dark:text-indigo-400">
                                        {pole.seq || '-'}
                                    </td>
                                    <td className="px-3 py-2 font-medium text-indigo-600 dark:text-indigo-400">
                                        {pole.no || '-'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {pole.is_pip === true
                                            ? 'Yes'
                                            : pole.is_pip === false
                                              ? 'No'
                                              : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {pole.latitude || '0.00'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {pole.longitude || '0.00'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {pole.type_name ||
                                            pole.pole_type_name ||
                                            '-'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-[10px] font-medium">
                                            {pole.status_name || 'Completed'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="w-1/3 min-w-[200px] flex flex-col">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-40 w-full flex items-center justify-center text-gray-400 border border-gray-300 dark:border-gray-600 relative overflow-hidden">
                        {!isLoaded ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                                Loading Map...
                            </div>
                        ) : (
                            <GoogleMap
                                zoom={10}
                                center={mapCenter}
                                mapContainerStyle={{
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                {poles.map((item, idx) =>
                                    item.latitude && item.longitude ? (
                                        <Marker
                                            key={item.id || idx}
                                            position={{
                                                lat: Number(item.latitude),
                                                lng: Number(item.longitude),
                                            }}
                                            label={{
                                                text: `Pole - ${item.no || item.seq || '-'}`,
                                                className: 'marker-label',
                                            }}
                                        />
                                    ) : null,
                                )}
                            </GoogleMap>
                        )}
                    </div>
                    {/* <button
                        onClick={() =>
                            props.onOpenMap && props.onOpenMap(poles)
                        }
                        className="mt-2 w-full py-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                    >
                        View Poles on Map <span className="ml-1">↗</span>
                    </button> */}
                </div>
            </div>
        </div>
    );
};

interface PoleManagementTableProps {
    filters?: any;
    onDataLoaded?: (data: any[]) => void;
}

const PoleManagementTable = ({
    filters = {},
    onDataLoaded,
}: PoleManagementTableProps) => {
    const dispatch = useAppDispatch();
    const gridRef = useRef<any>(null);
    const filtersRef = useRef(filters);

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    const [showMapModal, setShowMapModal] = useState<boolean>(false);
    const [mapPoles, setMapPoles] = useState<any[]>([]);

    const onOpenMap = useCallback((poles: any[]) => {
        setMapPoles(poles);
        setShowMapModal(true);
    }, []);

    const [columnDefs] = useState([
        {
            field: 'number_job',
            headerName: 'Job Number',
            cellRenderer: 'agGroupCellRenderer',
            minWidth: 200,
            cellRendererParams: {
                suppressCount: true,
            },
        },
        { field: 'koj', headerName: 'KOJ' },
        {
            field: 'request_date',
            headerName: 'Request Date',
            valueFormatter: (params: any) => {
                if (!params.value) return '-';
                return new Date(params.value).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
            },
        },
        {
            field: 'complete_date',
            headerName: 'Complete Date',
            valueFormatter: (params: any) => {
                if (!params.value) return '-';
                return new Date(params.value).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
            },
        },
        { field: 'number_id', headerName: 'ID Number' },
        { field: 'project', headerName: 'Project' },
        {
            field: 'status',
            headerName: 'Status',
            cellRenderer: (props: any) => {
                return (
                    <div className="flex items-center h-full">
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                            Completed
                        </span>
                    </div>
                );
            },
        },
        { field: 'type', headerName: 'Type' },
        { field: 'category_name', headerName: 'Category' },
        { field: 'district', headerName: 'District' },
        { field: 'other_number', headerName: 'Other Number' },
        {
            field: 'due_date',
            headerName: 'Due Date',
            valueFormatter: (params: any) => {
                if (!params.value) return '-';
                return new Date(params.value).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
            },
        },
        { field: 'request_pole', headerName: 'Poles' },
        {
            headerName: 'Action',
            field: 'actions',
            sortable: false,
            pinned: 'right',
            minWidth: 100,
            cellClass: 'text-center',
            headerClass: 'text-center',
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            cellRenderer: (props: any) => {
                return (
                    <div className="flex items-center gap-2 w-full justify-center">
                        <Link
                            to={
                                props.data?.koj === 'MRE' ||
                                props.data?.koj === 'MRE Revision'
                                    ? `/worksheet/mre/mre-detail/${props.data?.id}`
                                    : props.data?.koj === 'Katapult'
                                      ? `/worksheet/katapult/katapult-detail/${props.data?.id}`
                                      : `/worksheet/ocalc/job-detail/${props.data?.id}`
                            }
                            className="text-gray-400 hover:text-indigo-600 transition-colors"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        </Link>
                        {/* <button className="text-gray-400 hover:text-indigo-600 transition-colors">
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                            </svg>
                        </button> */}
                    </div>
                );
            },
        },
    ]);

    const getServerSideDataSource = useCallback(() => {
        return {
            getRows: (params: any) => {
                // Determine sort column based on params
                let sortCol = JobHistoryColumnSort.request_date;
                let sortType = SortingEnum.Descending;

                if (
                    params.request.sortModel &&
                    params.request.sortModel.length > 0
                ) {
                    const sortModel = params.request.sortModel[0];
                    if (sortModel.sort === 'asc')
                        sortType = SortingEnum.Ascending;

                    switch (sortModel.colId) {
                        case 'number_job':
                            sortCol = JobHistoryColumnSort.number_job;
                            break;
                        case 'district':
                            sortCol = JobHistoryColumnSort.district;
                            break;
                        case 'complete_date':
                            sortCol = JobHistoryColumnSort.complete_date;
                            break;
                        // Map other columns if needed
                        default:
                            break;
                    }
                }
                const skip = params.request.startRow;
                const take = params.api.paginationGetPageSize();

                const { completeStartDate, completeEndDate, ...restFilters } =
                    filters;

                dispatch(
                    getHistory({
                        skip,
                        take,
                        column: sortCol,
                        sort: sortType,
                        startCompleteDate: completeStartDate,
                        endCompleteDate: completeEndDate,
                        ...restFilters,
                    }),
                )
                    .then((res: any) => {
                        if (
                            res.payload &&
                            res.payload.data &&
                            res.payload.data.list
                        ) {
                            if (onDataLoaded) {
                                onDataLoaded(res.payload.data.list);
                            }
                            params.success({
                                rowData: res.payload.data.list,
                                rowCount:
                                    res.payload.rows ||
                                    res.payload.data.list.length,
                            });
                        } else {
                            params.fail();
                        }
                    })
                    .catch(() => {
                        params.fail();
                    });
            },
        };
    }, [dispatch, filters]);

    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.setServerSideDatasource(
                getServerSideDataSource(),
            );
        }
    }, [filters, getServerSideDataSource]);

    return (
        <div className="w-full">
            {/* <div className="flex justify-between items-center mb-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Completed Jobs{' '}
                    <span className="text-gray-400 text-xs font-normal">
                        (Group by Job No)
                    </span>
                </h2>
            </div> */}

            <AgDataGridServerPaging
                ref={gridRef}
                columnDefs={columnDefs}
                getServerSideDataSource={getServerSideDataSource}
                isRowSelectable={() => true}
                onSelectionChanged={() => {}}
                tableDimension={{
                    width: '100%',
                    height: '80vh',
                }}
                masterDetail={true}
                detailCellRenderer={PoleDetailRenderer}
                detailCellRendererParams={{
                    onOpenMap,
                    getFilters: () => filtersRef.current,
                }}
                detailRowHeight={250}
                rowSelection="multiple"
                defaultPageSize={20}
            />

            <DefaultModal
                title="View Poles on Map"
                show={showMapModal}
                onClose={() => setShowMapModal(false)}
                size="7xl"
                body={
                    <div style={{ height: '80vh', width: '100%' }}>
                        <FullScreenMap poles={mapPoles} />
                    </div>
                }
            />
        </div>
    );
};

const FullScreenMap = ({ poles = [] }: { poles: any[] }) => {
    const libraries: Libraries = useMemo(() => ['places'], []);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: 'AIzaSyDQ-aesOz_ut1yBv_d6FG-0N60uqDwgLZs',
        libraries: libraries,
    });

    const mapCenter = useMemo(() => {
        if (poles.length > 0 && poles[0].latitude && poles[0].longitude) {
            return {
                lat: Number(poles[0].latitude),
                lng: Number(poles[0].longitude),
            };
        }
        return { lat: 43.45, lng: -80.49 };
    }, [poles]);

    if (!isLoaded)
        return (
            <div className="flex items-center justify-center h-full w-full text-gray-500">
                Loading Map...
            </div>
        );

    return (
        <GoogleMap
            zoom={10}
            center={mapCenter}
            mapContainerStyle={{ width: '100%', height: '100%' }}
        >
            {poles.map((item, idx) =>
                item.latitude && item.longitude ? (
                    <Marker
                        key={item.id || idx}
                        position={{
                            lat: Number(item.latitude),
                            lng: Number(item.longitude),
                        }}
                        label={{
                            text: `Pole - ${item.no || item.seq || '-'}`,
                            className: 'marker-label',
                        }}
                    />
                ) : null,
            )}
        </GoogleMap>
    );
};

export default PoleManagementTable;
